import { useMemo, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/auth.store';
import {
  notificationsService,
  type NotificationItem,
} from '../services/notifications.service';

export type NotificationFilter = 'all' | 'unread';

const PAGE_SIZE = 15;

/**
 * Rich data hook for the notifications panel: paginated (infinite) list with an
 * All/Unread filter, accurate unread count, and mark-read / mark-all-read /
 * delete actions. Invalidates the shared ['notifications'] cache so the header
 * bell badge stays in sync.
 */
export function useNotificationsPanel() {
  const qc = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const enabled = isAuthenticated && !!accessToken;
  const [filter, setFilter] = useState<NotificationFilter>('all');

  const listQuery = useInfiniteQuery({
    queryKey: ['notifications-panel', filter],
    enabled,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      notificationsService.listPage(
        pageParam,
        PAGE_SIZE,
        filter === 'unread' ? 'DELIVERED' : undefined,
      ),
    getNextPageParam: (last) => (last.hasNext ? last.page + 1 : undefined),
  });

  const countsQuery = useQuery({
    queryKey: ['notifications-counts'],
    queryFn: () => notificationsService.counts(),
    enabled,
    staleTime: 30_000,
  });

  const items: NotificationItem[] = useMemo(
    () => listQuery.data?.pages.flatMap((p) => p.data) ?? [],
    [listQuery.data],
  );

  const unreadCount = countsQuery.data?.unread ?? 0;

  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: ['notifications-panel'] });
    void qc.invalidateQueries({ queryKey: ['notifications-counts'] });
    void qc.invalidateQueries({ queryKey: ['notifications'] }); // header bell
  };

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsService.markAsRead(id),
    onSuccess: invalidateAll,
  });

  const markAll = useMutation({
    mutationFn: () => notificationsService.markAllRead(),
    onSuccess: () => {
      invalidateAll();
      toast.success('All notifications marked as read.');
    },
    onError: () => toast.error('Could not mark all as read.'),
  });

  // DELETE /notifications/:id is a hard delete — there is no DISMISSED status
  // and no soft delete behind it, so this is irreversible.
  const deleteNotification = useMutation({
    mutationFn: (id: string) => notificationsService.remove(id),
    onSuccess: invalidateAll,
    onError: () => toast.error('Could not delete notification.'),
  });

  return {
    filter,
    setFilter,
    items,
    unreadCount,
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    refetch: () => void listQuery.refetch(),
    hasNextPage: listQuery.hasNextPage,
    isFetchingNextPage: listQuery.isFetchingNextPage,
    fetchNextPage: () => void listQuery.fetchNextPage(),
    markRead: (id: string) => markRead.mutate(id),
    markAll: () => markAll.mutate(),
    isMarkingAll: markAll.isPending,
    deleteNotification: (id: string) => deleteNotification.mutate(id),
  };
}
